import { useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
    useGetVehicleStockListQuery,
    useSellVehicleStockToCustomerMutation
} from '../../../../api/endpoints/vehicleOrder';
import { useAddCustomerMutation } from '../../../../api/customerApi';
import { toastService } from '../../../../utils/toastService';

const initialCustomerForm = {
    customerType: '1',
    country: { name: 'Türkiye', text: 'Türkiye', code: 'TR' },
    chassis: '',
    plate: '',
    firstName: '',
    lastName: '',
    tcNumber: '',
    phone: '',
    address: '',
    city: '',
    chassisList: [],
    selectedPlate: ''
};

const initialErrors = {
    customer: '',
    stock: '',
    price: ''
};

const useSellToCustomer = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();

    const [customerId, setCustomerId] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [stockId, setStockId] = useState(null);
    const [price, setPrice] = useState(null);
    const [description, setDescription] = useState('');
    const [errors, setErrors] = useState(initialErrors);

    const [customerDefVisible, setCustomerDefVisible] = useState(false);
    const [customerFormData, setCustomerFormData] = useState(initialCustomerForm);
    const [customerErrors, setCustomerErrors] = useState({});
    const [newCustomer, setNewCustomer] = useState(null);

    const { data: stockData = [], isFetching: isStockLoading } = useGetVehicleStockListQuery({ languageId });
    const [sellToCustomer, { isLoading: isSelling }] = useSellVehicleStockToCustomerMutation();
    const [createCustomer, { isLoading: isCreatingCustomer }] = useAddCustomerMutation();

    const stockOptions = useMemo(
        () =>
            (stockData || []).map((row) => ({
                value: row.id,
                label: [(row.chassisNo || '').trim(), [row.brand, row.model].filter(Boolean).join(' / ')]
                    .filter(Boolean)
                    .join(' - '),
                row
            })),
        [stockData]
    );

    const selectedStock = useMemo(
        () => (stockData || []).find((row) => row.id === stockId) || null,
        [stockData, stockId]
    );

    const handleCustomerChange = useCallback((id, fullData) => {
        setCustomerId(id);
        setCustomerData(fullData || null);
        setErrors((prev) => ({ ...prev, customer: '' }));
    }, []);

    const handleStockChange = useCallback((value) => {
        setStockId(value);
        setErrors((prev) => ({ ...prev, stock: '' }));
    }, []);

    const handlePriceChange = useCallback((value) => {
        setPrice(value);
        setErrors((prev) => ({ ...prev, price: '' }));
    }, []);

    const handleDescriptionChange = useCallback((value) => {
        setDescription(value);
    }, []);

    const resetForm = useCallback(() => {
        setCustomerId(null);
        setCustomerData(null);
        setStockId(null);
        setPrice(null);
        setDescription('');
        setErrors(initialErrors);
        setNewCustomer(null);
    }, []);

    const addChassis = useCallback((chassisNo, licensePlate) => {
        if (!chassisNo) {
            return { success: false, message: th('customerDefinition.validation.chassisRequired') };
        }

        const isDuplicate = customerFormData.chassisList?.some((item) => item.chassisNo === chassisNo);
        if (isDuplicate) {
            return { success: false, message: th('customerDefinition.duplicateChassis') };
        }

        const updatedList = [...(customerFormData.chassisList || []), { chassisNo, licensePlate: licensePlate || '' }];
        setCustomerFormData((prev) => ({
            ...prev,
            chassisList: updatedList,
            chassis: '',
            selectedPlate: ''
        }));

        return { success: true };
    }, [customerFormData.chassisList, th]);

    const removeChassis = useCallback((chassisNo) => {
        setCustomerFormData((prev) => ({
            ...prev,
            chassisList: (prev.chassisList || []).filter((item) => item.chassisNo !== chassisNo)
        }));
    }, []);

    const handleOpenCustomerDefinition = useCallback(() => {
        setCustomerFormData({
            ...initialCustomerForm,
            chassis: (selectedStock?.chassisNo || '').trim()
        });
        setCustomerErrors({});
        setCustomerDefVisible(true);
    }, [selectedStock]);

    const handleCloseCustomerDefinition = useCallback(() => {
        setCustomerDefVisible(false);
    }, []);

    const handleSaveNewCustomer = useCallback(async () => {
        const isCompany = String(customerFormData.customerType || '1') === '2';
        const nextErrors = {};

        if (!customerFormData.firstName) {
            nextErrors.firstName = th('common.required');
        }
        if (!customerFormData.phone) {
            nextErrors.phone = th('common.required');
        }
        if (!isCompany && (!customerFormData.chassisList || customerFormData.chassisList.length === 0)) {
            nextErrors.chassisList = th('customerDefinition.validation.chassisListRequired');
        }

        if (Object.keys(nextErrors).length > 0) {
            setCustomerErrors(nextErrors);
            return;
        }

        try {
            const result = await createCustomer(customerFormData).unwrap();
            toastService.success(th('customerDefinition.addSuccess'));
            setCustomerDefVisible(false);

            if (result?.id) {
                const toText = (val) => (val !== undefined && val !== null ? String(val) : '');
                const createdCustomer = {
                    id: result.id,
                    name: result.name || customerFormData.firstName || '',
                    surname: result.surname || customerFormData.lastName || '',
                    identityNo: result.identityNo || toText(customerFormData.tcNumber),
                    phoneNumber: result.phoneNumber || toText(customerFormData.phone),
                    address: result.address || customerFormData.address || '',
                    country: result.country || customerFormData.country?.text || customerFormData.country?.name || (typeof customerFormData.country === 'string' ? customerFormData.country : '') || '',
                    city: result.city || customerFormData.city || '',
                    licensePlate: customerFormData.chassisList?.[0]?.licensePlate || ''
                };
                setNewCustomer(createdCustomer);
                setCustomerId(result.id);
                setCustomerData(createdCustomer);
                setErrors((prev) => ({ ...prev, customer: '' }));
            }
        } catch (err) {
            void err;
        }
    }, [customerFormData, createCustomer, th]);

    const validate = useCallback(() => {
        const nextErrors = {
            customer: !customerId ? th('vehicleSalesSellToCustomer.validation.customerRequired') : '',
            stock: !stockId ? th('vehicleSalesSellToCustomer.validation.chassisRequired') : '',
            price: !price || price <= 0 ? th('vehicleSalesSellToCustomer.validation.priceInvalid') : ''
        };
        setErrors(nextErrors);
        return !nextErrors.customer && !nextErrors.stock && !nextErrors.price;
    }, [customerId, stockId, price, th]);

    const handleSave = useCallback(async () => {
        if (!validate()) {
            return;
        }

        const body = { stockId, customerId, price, languageId };
        if (description?.trim()) {
            body.description = description.trim();
        }

        try {
            const res = await sellToCustomer(body).unwrap();
            if (res?.isSuccess || res?.isSuccessful) {
                toastService.success(res.message || th('vehicleSalesSellToCustomer.toast.success'));
                resetForm();
            }
        } catch (err) {
            void err;
        }
    }, [validate, stockId, customerId, price, languageId, description, sellToCustomer, th, resetForm]);

    return {
        th,
        customerId,
        customerData,
        stockId,
        price,
        description,
        errors,
        stockOptions,
        selectedStock,
        isStockLoading,
        isSelling,
        handleCustomerChange,
        handleStockChange,
        handlePriceChange,
        handleDescriptionChange,
        handleSave,
        resetForm,
        customerDefVisible,
        customerFormData,
        setCustomerFormData,
        customerErrors,
        newCustomer,
        isCreatingCustomer,
        addChassis,
        removeChassis,
        handleOpenCustomerDefinition,
        handleCloseCustomerDefinition,
        handleSaveNewCustomer
    };
};

export default useSellToCustomer;
